import { AgencyQuickReplyDto } from '@bandido/shared/dtos';

export const WHATSAPP_SLASH_LISTBOX_ID = 'whatsapp-slash-command-listbox';
export const WHATSAPP_QUICK_REPLIES_COMMAND_OPTION_ID =
  'whatsapp-slash-option-quick-replies';

export type SlashPaletteStage = 'commands' | 'quickReplies';

export type SlashParseResult =
  | {
      active: false;
      mode: null;
      token: '';
      query: '';
      exactAlias: null;
      discoveryQuery: '';
    }
  | {
      active: true;
      mode: SlashPaletteStage;
      token: string;
      query: string;
      exactAlias: string | null;
      discoveryQuery: string;
    };

export type SlashEnterAction =
  | { type: 'none' }
  | { type: 'openQuickReplies'; nextValue: string }
  | { type: 'selectQuickReply'; quickReply: AgencyQuickReplyDto };

export const parseSlashCommand = (
  value: string,
  aliases: string[],
): SlashParseResult => {
  if (!value.startsWith('/')) {
    return {
      active: false,
      mode: null,
      token: '',
      query: '',
      exactAlias: null,
      discoveryQuery: '',
    };
  }

  const firstWhitespaceIndex = value.search(/\s/);
  const token =
    firstWhitespaceIndex === -1 ? value : value.slice(0, firstWhitespaceIndex);
  const query =
    firstWhitespaceIndex === -1 ? '' : value.slice(firstWhitespaceIndex + 1);
  const normalizedToken = token.toLowerCase();
  const exactAlias = aliases.find((alias) => alias === normalizedToken) || null;

  return {
    active: true,
    mode: exactAlias ? 'quickReplies' : 'commands',
    token,
    query,
    exactAlias,
    discoveryQuery: normalizedToken.replace(/^\//, ''),
  };
};

export const getCommandDiscoveryText = ({
  label,
  aliases,
}: {
  label: string;
  aliases: string[];
}) => [label, ...aliases].join(' ').toLowerCase();

export const shouldShowQuickRepliesCommand = ({
  slashState,
  commandDiscoveryText,
}: {
  slashState: SlashParseResult;
  commandDiscoveryText: string;
}) =>
  slashState.active &&
  slashState.mode === 'commands' &&
  (!slashState.discoveryQuery ||
    commandDiscoveryText.includes(slashState.discoveryQuery));

export const filterEnabledQuickReplies = ({
  quickReplies,
  slashState,
}: {
  quickReplies: AgencyQuickReplyDto[] | undefined;
  slashState: SlashParseResult;
}) => {
  const enabledQuickReplies =
    quickReplies?.filter((quickReply) => quickReply.enabled) || [];

  if (!slashState.active || slashState.mode !== 'quickReplies') {
    return enabledQuickReplies;
  }

  const normalizedQuery = slashState.query.trim().toLowerCase();

  if (!normalizedQuery) {
    return enabledQuickReplies;
  }

  return enabledQuickReplies.filter((quickReply) =>
    `${quickReply.slug} ${quickReply.name} ${quickReply.body}`
      .toLowerCase()
      .includes(normalizedQuery),
  );
};

export const getVisibleSlashItemsCount = ({
  slashState,
  showQuickRepliesCommand,
  quickReplies,
}: {
  slashState: SlashParseResult;
  showQuickRepliesCommand: boolean;
  quickReplies: AgencyQuickReplyDto[];
}) =>
  slashState.mode === 'commands'
    ? showQuickRepliesCommand
      ? 1
      : 0
    : quickReplies.length;

export const getNextSlashActiveIndex = ({
  currentIndex,
  itemCount,
  direction,
}: {
  currentIndex: number;
  itemCount: number;
  direction: 'next' | 'previous';
}) => {
  if (itemCount <= 0) {
    return 0;
  }

  if (direction === 'next') {
    return currentIndex + 1 >= itemCount ? 0 : currentIndex + 1;
  }

  return currentIndex - 1 < 0 ? itemCount - 1 : currentIndex - 1;
};

export const getCanonicalQuickRepliesComposerValue = ({
  currentValue,
  slashState,
  canonicalAlias,
}: {
  currentValue: string;
  slashState: SlashParseResult;
  canonicalAlias: string;
}) =>
  slashState.active && slashState.exactAlias
    ? currentValue
    : `${canonicalAlias} `;

export const clearSlashComposerValue = () => '';

export const getSlashEnterAction = ({
  currentValue,
  slashState,
  showQuickRepliesCommand,
  activeIndex,
  quickReplies,
  canonicalAlias,
}: {
  currentValue: string;
  slashState: SlashParseResult;
  showQuickRepliesCommand: boolean;
  activeIndex: number;
  quickReplies: AgencyQuickReplyDto[];
  canonicalAlias: string;
}): SlashEnterAction => {
  if (!slashState.active) {
    return { type: 'none' };
  }

  if (slashState.mode === 'commands') {
    if (!showQuickRepliesCommand) {
      return { type: 'none' };
    }

    return {
      type: 'openQuickReplies',
      nextValue: getCanonicalQuickRepliesComposerValue({
        currentValue,
        slashState,
        canonicalAlias,
      }),
    };
  }

  const selectedQuickReply = quickReplies[activeIndex];

  if (!selectedQuickReply) {
    return { type: 'none' };
  }

  return { type: 'selectQuickReply', quickReply: selectedQuickReply };
};

export const getQuickReplyOptionId = (quickReplyId: number) =>
  `whatsapp-slash-option-quick-reply-${quickReplyId}`;

export const getSlashActiveOptionId = ({
  slashState,
  showQuickRepliesCommand,
  activeIndex,
  quickReplies,
}: {
  slashState: SlashParseResult;
  showQuickRepliesCommand: boolean;
  activeIndex: number;
  quickReplies: AgencyQuickReplyDto[];
}) => {
  if (!slashState.active) {
    return undefined;
  }

  if (slashState.mode === 'commands') {
    return showQuickRepliesCommand
      ? WHATSAPP_QUICK_REPLIES_COMMAND_OPTION_ID
      : undefined;
  }

  const activeQuickReply = quickReplies[activeIndex];

  if (!activeQuickReply) {
    return undefined;
  }

  return getQuickReplyOptionId(activeQuickReply.id);
};
